/**
 * Italian message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: tu (locked in l10n/locales.md).
 *
 * Platform terms follow Italian UI conventions (impostazioni,
 * associare for pairing, duplicazione for notification mirroring —
 * parallel to fr, cancellare for dismiss per Android-it "Cancella
 * tutto", Appunti for clipboard). "Glance" is the untranslated feature
 * brand name (glossary D1); "clip" is the D2 loanword (natural in it).
 *
 * Plural notes: Italian cardinal categories are {one, many, other} with
 * `many` millions-only — `selectPlural` calls supply {one, other}.
 * Italian `one` is exactly 1 (unlike fr/pt), so "0 risultati" renders
 * plural, the standard Italian behavior.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Connesso",
    statusReconnecting: "Riconnessione…",
    statusDisconnected: "Disconnesso",
    openSettings: "Apri impostazioni",
    pairedFallbackDevice: "il tuo telefono",
    publicModeToggleEnable: "Nascondi il contenuto delle notifiche",
    publicModeToggleDisable: "Mostra il contenuto delle notifiche",
  },

  notifications: {
    replyAriaLabel: "Rispondi",
    replyPlaceholder: "Rispondi…",
    replySend: "Invia",
    sent: "Inviato",
    failedToSend: "Invio non riuscito",
    unread: "Da leggere",
    dismiss: "Cancella",
    notificationImageAlt: "Immagine della notifica",
  },

  conversations: {
    replyAriaLabel: "Rispondi",
    replyPlaceholder: "Rispondi…",
    replySend: "Invia",
    sent: "Inviato",
    failedToSend: "Invio non riuscito",
    dismiss: "Cancella conversazione",
    messagesListLabel: "Messaggi di questa conversazione",
    deviceOwner: "Tu",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Nessun messaggio da leggere"
        : `${count} ${selectPlural("it", count, {
            one: "messaggio da leggere",
            other: "messaggi da leggere",
          })}`,
  },

  list: {
    today: "Oggi",
    yesterday: "Ieri",
    searchNotifications: "Cerca notifiche",
    clearSearch: "Cancella ricerca",
    clearAll: "Cancella tutto",
    markAllRead: "Segna tutte come lette",
    groupCheckboxLabel: "Raggruppa",
    noNotificationsTitle: "Ancora nessuna notifica",
    noNotificationsDesc: "I nuovi messaggi duplicati dal tuo telefono appariranno qui.",
    noMatchesTitle: "Nessun risultato",
    notificationsList: "Notifiche",
    privacyAppList: "Notifiche per app",
    clear: "Cancella",
    clearMatches: (count: number): string =>
      `Cancella ${count} ${selectPlural("it", count, { one: "risultato", other: "risultati" })}`,
    noMatchesDesc: (query: string): string => `Nessuna notifica corrisponde a "${query}".`,
    appNotificationsGroup: (appName: string): string => `Notifiche di ${appName}`,
    dismissAllFrom: (appName: string): string => `Cancella tutte le notifiche di ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("it", count, { one: "messaggio", other: "messaggi" })
          : kind === "calls"
            ? selectPlural("it", count, { one: "chiamata", other: "chiamate" })
            : selectPlural("it", count, { one: "notifica", other: "notifiche" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "cancella",
    kbdNavigate: "naviga",
    kbdOrSeparator: "o",
  },

  settings: {
    back: "Torna alle notifiche",
    settings: "Impostazioni",
    general: "Generali",
    theme: "Tema",
    themeAuto: "Automatico",
    themeLight: "Chiaro",
    themeDark: "Scuro",
    language: "Lingua",
    languageSystemDefault: "Predefinita di sistema",
    notificationsSection: "Notifiche",
    osNotifications: "Notifiche di sistema",
    callNotifications: "Notifiche di chiamata",
    privacySecurity: "Privacy",
    analyticsToggle: "Statistiche di utilizzo anonime",
    analyticsToggleDescription: "Solo conteggi e traguardi — mai le tue notifiche.",
    statusConnected: "Connesso",
    statusReconnecting: "Riconnessione…",
    statusDisconnected: "Disconnesso",
    disconnectFromPhone: "Disconnetti dal telefono",
    disconnectConfirm: "Vuoi davvero farlo? Il dispositivo verrà dissociato.",
    yesDisconnect: "Sì, disconnetti",
    cancel: "Annulla",
    version: "Versione",
    encryption: "Crittografia",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Ti serve aiuto? Scrivi a __EMAIL__",
    disconnectFailed: "Disconnessione non riuscita: riprova",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Disconnesso, ma la pulizia locale non è riuscita: ${errorMsg}. Ricarica la pagina.`,
    disconnectLocalCleanupFailed:
      "Disconnesso, ma la pulizia locale non è riuscita. Ricarica la pagina.",
  },

  pairing: {
    enterCodeInstruction: "Inserisci questo codice nell'app Reflecto",
    pairedSuccessfully: "Associazione riuscita!",
    pairingFailed: "Associazione non riuscita",
    requestingPairingCode: "Richiesta del codice di associazione…",
    codeExpired: "Codice scaduto",
    tryAgain: "Riprova",
    failedToStartPairing: "Impossibile avviare l'associazione",
    installHeading: "Non hai ancora l'app?",
    installScanHint: "Inquadralo con la fotocamera del telefono",
    installQrLabel: "Codice QR per installare Reflecto sul telefono",
    capabilitiesHeading: "Dopo l'associazione potrai:",
    capabilityMirror: "Vedere qui le notifiche del telefono",
    capabilityReply: "Rispondere senza prendere in mano il telefono",
    capabilityShare: "Condividere gli appunti e i file in entrambe le direzioni",
    encryptionNote: "Crittografia end-to-end. Il server non vede mai i tuoi contenuti.",
  },

  dnd: {
    title: "Non disturbare",
    description: "Sospendi le notifiche su questo dispositivo per la prossima ora.",
    snooze1h: "Sospendi per 1 ora",
    snoozeActiveUntil: (formattedTime: string): string => `Attivo fino alle ${formattedTime}`,
    cancelSnooze: "Annulla",
  },

  connectors: {
    title: "Servizi collegati",
    emptyBody:
      "Ancora nessun servizio collegato. Collega ChatGPT o Claude dalle loro impostazioni dei connettori, poi approva il codice sul telefono.",
    scopeSend: "Può inviare notifiche",
    added: (date: string): string => `Aggiunto ${date}`,
    lastUsed: (relative: string): string => `Ultimo utilizzo ${relative}`,
    neverUsed: "Mai usato",
    revoke: "Scollega",
    revokeConfirm: (label: string): string =>
      `Scollegare ${label}? Perderà l'accesso immediatamente.`,
    revokeAction: "Scollega",
    revokeConfirmLabel: (label: string): string => `Scollega ${label}`,
    cancel: "Annulla",
    loadError: "Impossibile caricare i servizi collegati. Riprova.",
    revokeError: "Impossibile scollegare il servizio. Riprova.",
    retry: "Riprova",
  },

  safetyNumber: {
    rowLabel: "Verifica crittografia",
    rowSublabel: "Confronta i numeri di sicurezza con il tuo telefono",
    cardTitle: "Numero di sicurezza",
    caption: (deviceLabel: string): string =>
      `Confronta con il numero mostrato su ${deviceLabel}. Se entrambi coincidono, la tua crittografia end-to-end è intatta.`,
    repairNote: "Questo numero cambia quando associi di nuovo un dispositivo.",
    copy: "Copia",
    copied: "Copiato",
    maskedHint: "Nascosto mentre la modalità privacy è attiva",
  },

  feedback: {
    title: "Invia feedback",
    close: "Chiudi",
    messageLabel: "Il tuo messaggio",
    messagePlaceholder: "Hai trovato un bug, hai un'idea o qualcosa non ti convince? Raccontacelo.",
    categoryLabel: "Categoria",
    categoryBug: "Bug",
    categoryIdea: "Idea",
    categoryOther: "Altro",
    emailLabel: "E-mail (facoltativa, solo se vuoi una risposta)",
    emailPlaceholder: "tu@esempio.it",
    emailHelp: "Non ti aggiungeremo a nessuna lista.",
    disclosure:
      "Il tuo messaggio viene inviato al server di Reflecto e registrato nel nostro sistema interno di tracciamento. Alleghiamo la versione dell'app e un ID dispositivo assegnato durante l'associazione per correlare le segnalazioni. Non vediamo le tue notifiche, i contatti né alcun dato personale. L'e-mail è facoltativa: indicala solo se vuoi una risposta. Non ti aggiungeremo a nessuna lista.",
    submit: "Invia feedback",
    sending: "Invio in corso…",
    success: "Grazie! Verrà letto da una persona, di solito entro 24 ore",
    error: "Invio non riuscito. Riprova.",
    done: "Fine",
    sendFeedback: "Invia feedback",
  },

  uninstall: {
    headline: "L'estensione Reflecto è stata disinstallata",
    body: "Non c'è mai stato un account e l'estensione teneva la tua associazione su questo computer, quindi non è rimasto nulla da ripulire.",
    ask: "Se hai trenta secondi, ci piacerebbe sapere che cosa non ha funzionato.",
    categoryLegend: "Che tipo di feedback è?",
    categoryBug: "Qualcosa si è rotto",
    categoryIdea: "Mancava qualcosa che mi serviva",
    categoryOther: "Altro",
    messageLabel: "Che cosa non ha funzionato?",
    messagePlaceholder: "Dopo qualche ora le notifiche hanno smesso di arrivare.",
    messageRequired: "Scrivi prima una frase, poi invia.",
    charactersLeft: (count: number): string => `${count} caratteri rimasti`,
    emailLabel: "E-mail (facoltativa)",
    emailPlaceholder: "tu@esempio.it",
    emailHelp: "Serve solo per risponderti. Nient'altro.",
    submit: "Invia feedback",
    sending: "Invio in corso…",
    retry: "Riprova",
    privacyNote:
      "Questo modulo non è collegato a nessun dispositivo. Non contiene le tue notifiche, i tuoi appunti né i tuoi file. L'e-mail è facoltativa e serve solo per rispondere.",
    success: "Grazie. Ognuno di questi messaggi viene letto da una persona.",
    errorGeneric: "Non è stato inviato. Il tuo messaggio è ancora qui.",
    errorRateLimited:
      "Hai inviato diversi messaggi di recente. Riprova tra circa un'ora. Il tuo testo resta qui.",
    footerWebApp: "L'app web di Reflecto funziona anche senza l'estensione.",
    footerWebAppLink: "Apri l'app web",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Chiudi",
    aiSummaryTag: "Riepilogo IA",
    errorAi: "Impossibile generare un riepilogo al momento.",
    retryCta: "Riprova",
    downloadHeading: "Ricevi riepiloghi IA",
    downloadBody:
      "Recupera ciò che hai perso con un riepilogo IA generato sul dispositivo. Tutto gira sul tuo dispositivo.",
    downloadCta: "Scarica il modello",
    downloadPrivacyNote: "Un solo download. Il server non vede mai le tue notifiche.",
    downloadingHeading: "Download del modello…",
    caughtUp: "Sei in pari: al momento niente richiede la tua attenzione.",
    needsYou: "Da gestire",
    quiet: "Il resto",
    scanning: (pending: number): string => `Riepilogo di ${pending} in corso…`,
    jump: "Apri notifica",
    affordanceReply: "Rispondi",
    affordanceOpen: "Apri",
    affordanceCopyOtp: "Copia codice",
    buttonLabel: "Glance",
  },

  update: {
    title: "Aggiornamento disponibile",
    description: "Una nuova versione di Reflecto è pronta. Ricarica per aggiornare.",
    reload: "Ricarica",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Abbiamo chiamato questo dispositivo "${name}".`,
    cardLooksGood: "Va bene",
    cardEdit: "Modifica",
    cardAriaLabel: "Conferma il nome del dispositivo",
    renameTitle: "Rinomina questo dispositivo",
    renameLabel: "Nome del dispositivo",
    renameSave: "Salva",
    renameCancel: "Annulla",
    renameFailed: "Impossibile rinominare. Riprova.",
  },

  collision: {
    title: (name: string): string => `Hai già un "${name}"`,
    subtitle: "Come vuoi chiamare questo?",
    chipWork: (name: string): string => `Lavoro ${name}`,
    chipHome: (name: string): string => `Casa ${name}`,
    chipPersonal: (name: string): string => `Personale ${name}`,
    customLabel: "Nome personalizzato",
    save: "Salva",
    useAnyway: "Usa comunque il nome originale",
    saveFailed: "Impossibile salvare il nuovo nome. Riprova.",
    ariaLabel: "Risolvi il nome dispositivo duplicato",
  },

  clipboard: {
    title: "Appunti",
    emptyTitle: "Ancora nessuna clip",
    empty: "Le clip dagli altri tuoi dispositivi appaiono qui. Tocca per copiare.",
    from: (label: string): string => `da ${label}`,
    to: (label: string): string => `a ${label}`,
    copy: "Copia negli appunti",
    copied: "Copiato",
    reveal: "Mostra",
    hide: "Nascondi",
    sensitiveChip: "Sensibile",
    cloudChip: "dal tuo token API",
    maskedHint: 'Contenuto nascosto: premi "Mostra" per vederlo',
    delete: "Elimina clip",
    sendPlaceholder: "Incolla il testo da inviare…",
    sendCta: "Invia",
    sendSensitiveToggle: "Contrassegna come sensibile",
    sendSuccess: (n: number): string =>
      `Inviato a ${n} ${selectPlural("it", n, { one: "dispositivo", other: "dispositivi" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Inviato a ${n} ${selectPlural("it", total, {
        one: "dispositivo su",
        other: "dispositivi su",
      })} ${total}.`,
    sendNoDevices: "Nessun dispositivo associato a cui inviare.",
    sendFailed: "Invio non riuscito. Riprova.",
    sendEmpty: "Niente da inviare.",
    dockRecentHeading: "Recenti",
    dockEmptyClipboard: "I tuoi appunti sono vuoti.",
    clearAll: "Cancella tutto",
    clearAllConfirm: "Sì, cancella tutto",
    clearAllCancel: "Annulla",
    shareTitle: "Invia ai tuoi dispositivi",
    shareReview:
      "Controlla cosa stai inviando. Andrà ai tuoi dispositivi associati, crittografato end-to-end.",
    shareSend: "Invia",
    shareCancel: "Annulla",
    shareNotPaired: "Associa prima un dispositivo per inviare il testo condiviso.",
    shareOpenApp: "Apri Reflecto",
    pasteFromClipboard: "Incolla dagli appunti",
    readFailed: "Impossibile leggere gli appunti: incolla qui sotto.",
  },
  install: {
    title: "Installa Reflecto",
    body: "Aggiungi alla schermata Home per un accesso più rapido.",
    cta: "Installa",
    dismissAria: "Chiudi la richiesta di installazione",
  },
  nav: {
    home: "Home",
    clipboard: "Appunti",
    files: "File",
    ariaLabel: "Navigazione principale",
    fileHiddenPublicMode: "Nascosto mentre la modalità privacy è attiva.",
  },
  common: {
    loading: "Caricamento",
  },
  errors: {
    notPaired: "Non associato",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} su ${os}`,
    webFallback: "Browser web",
  },
  palette: {
    placeholder: "Digita un comando…",
    noResults: "Nessun comando corrispondente",
    ariaLabel: "Palette dei comandi",
    clearAll: "Cancella tutte le notifiche",
    glance: "Riepilogo Glance",
    cancelSnooze: "Annulla sospensione",
    copyClip: "Copia l'ultima clip ricevuta",
  },

  fileTransfer: {
    sendTitle: "Invia un file",
    sendChooseFile: "Scegli file",
    sendDeviceLabel: "Invia a",
    sendPhoneFallbackName: "Telefono",
    send: "Invia file",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Preparazione…" : `Preparazione — ${pct}%`,
    sendDelivering: "In consegna al tuo telefono…",
    sendWaitingAccept: "In attesa che il tuo telefono accetti…",
    sendWaitingAppOpen: "Apri Reflecto sul tuo telefono per accettare",
    sendWaitingAppOpenHint:
      "Il tuo telefono non può ricevere notifiche push, quindi mostrerà questo file solo quando Reflecto sarà aperto.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Caricamento della parte ${partNumber} di ${partCount}…`,
    sendDone: "Inviato.",
    sendFailedRejected: "Il tuo telefono ha rifiutato il file.",
    sendFailedNoAnswer: "Nessuna risposta dal tuo telefono.",
    sendFailedUploadFailed: "Caricamento non riuscito. Riprova.",
    sendFailedCancelled: "Annullato.",
    sendFailedGeneric: "Qualcosa è andato storto. Riprova.",
    sendTooLarge: (maxMiB: number): string => `Il file è troppo grande (max ${maxMiB} MiB).`,
    sendKeepOpenHint: "Tieni aperta questa scheda finché il trasferimento non è completato.",
    sendAnother: "Invia un altro",
    sendNoDevice: "Associa prima un telefono per inviare file.",
    sendCancel: "Annulla",
    sendPeerProgress: (chunksDone: number): string =>
      `Il telefono ha ricevuto ${chunksDone} frammenti.`,

    receiveTitle: "File in arrivo",
    receiveOfferFrom: (sender: string): string => `${sender} vuole inviarti un file`,
    receiveAccept: "Accetta",
    receiveDecline: "Rifiuta",
    receiveReceiving: (n: number, total: number): string =>
      `Ricezione della parte ${n} di ${total}…`,
    receiveDone: "Salvato.",
    receiveFailed: "Trasferimento non riuscito.",
    receiveCancelled: "Annullato.",
    receiveCancel: "Annulla",

    errorChunkAuthFail: "Trasferimento non riuscito — impossibile verificare i dati. Riprova.",
    errorHashMismatch:
      "Trasferimento non riuscito — il file non ha superato il controllo di integrità. Riprova.",
    errorStorageFull: "Spazio di archiviazione insufficiente per questo trasferimento.",
    errorExpired: "Trasferimento scaduto.",
    errorQuotaExceeded: "Limite giornaliero di trasferimenti raggiunto — riprova domani.",
    errorInternal: "Trasferimento non riuscito — qualcosa è andato storto.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Ignora",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Avanzamento del trasferimento",
    outgoing: (n: number): string => `In uscita (${n})`,
    incoming: (n: number): string => `In arrivo (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Accetta tutto",
    dropTitle: "Trascina qui",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `File fino a ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Trascina qui o scegli un file da inviare",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Diretto",
    transportDirectHint:
      "Inviato da dispositivo a dispositivo — mai memorizzato sui nostri server.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Via server",
    transportRelayHint:
      "Instradato dai nostri server, sempre cifrato end-to-end — non possiamo leggerlo.",
    directBadge: "Diretto",
  },
  time: {
    justNow: "adesso",
    minutesAgo: (count: number): string => `${count} min fa`,
    hoursAgo: (count: number): string => `${count} h fa`,
  },
} satisfies MessagesShape;
